import { Injectable } from '@angular/core';

export interface StoredNotification {
  id: string;
  userId: string;
  type: string;
  title: string;
  body: string;
  data: any;
  timestamp: Date;
  read: boolean;
  caseId?: string;
  senderId?: string;
  senderName?: string;
}

/**
 * NotificationStorageService
 * Provides IndexedDB storage for offline notification history
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationStorageService {
  private readonly DB_NAME = 'CipherNotificationsDB';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'notifications';
  private db: IDBDatabase | null = null;

  constructor() {
    this.initDatabase();
  }

  /**
   * Initialize the IndexedDB database
   */
  private async initDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        console.warn('IndexedDB not available');
        resolve();
        return;
      }

      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;

        // Create notifications store
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          const store = db.createObjectStore(this.STORE_NAME, { keyPath: 'id' });
          store.createIndex('userId', 'userId', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('read', 'read', { unique: false });
          store.createIndex('caseId', 'caseId', { unique: false });
          store.createIndex('type', 'type', { unique: false });
        }
      };
    });
  }

  /**
   * Store a notification
   */
  async storeNotification(notification: StoredNotification): Promise<void> {
    await this.ensureDbReady();
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      
      const request = store.put(notification);
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all notifications for a user
   */
  async getNotifications(userId: string, limit: number = 100): Promise<StoredNotification[]> {
    await this.ensureDbReady();
    if (!this.db) return [];

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(this.STORE_NAME, 'readonly');
      const store = tx.objectStore(this.STORE_NAME);
      const index = store.index('userId');
      
      const request = index.getAll(userId, limit);
      
      request.onsuccess = () => {
        const results = request.result || [];
        // Sort by timestamp descending
        results.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        resolve(results);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get unread notifications count
   */
  async getUnreadCount(userId: string): Promise<number> {
    await this.ensureDbReady();
    if (!this.db) return 0;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(this.STORE_NAME, 'readonly');
      const store = tx.objectStore(this.STORE_NAME);
      const index = store.index('userId');
      
      const request = index.getAll(userId);
      
      request.onsuccess = () => {
        const results = request.result || [];
        const unread = results.filter(n => !n.read).length;
        resolve(unread);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(id: string): Promise<void> {
    await this.ensureDbReady();
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      
      const getRequest = store.get(id);
      
      getRequest.onsuccess = () => {
        const notification = getRequest.result;
        if (notification) {
          notification.read = true;
          const putRequest = store.put(notification);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve();
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Mark all notifications as read for a user
   */
  async markAllAsRead(userId: string): Promise<void> {
    await this.ensureDbReady();
    if (!this.db) return;

    const notifications = await this.getNotifications(userId);
    
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      
      let completed = 0;
      const total = notifications.length;
      
      if (total === 0) {
        resolve();
        return;
      }
      
      for (const notification of notifications) {
        notification.read = true;
        const request = store.put(notification);
        request.onsuccess = () => {
          completed++;
          if (completed === total) resolve();
        };
        request.onerror = () => reject(request.error);
      }
    });
  }

  /**
   * Delete a notification
   */
  async deleteNotification(id: string): Promise<void> {
    await this.ensureDbReady();
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      
      const request = store.delete(id);
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear old notifications (older than 30 days)
   */
  async clearOldNotifications(): Promise<void> {
    await this.ensureDbReady();
    if (!this.db) return;

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      const index = store.index('timestamp');
      
      const range = IDBKeyRange.upperBound(thirtyDaysAgo.toISOString());
      const request = index.openCursor(range);
      
      request.onsuccess = (event: any) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all notifications for a user
   */
  async clearAll(userId: string): Promise<void> {
    await this.ensureDbReady();
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      const index = store.index('userId');
      
      const request = index.openCursor(IDBKeyRange.only(userId));
      
      request.onsuccess = (event: any) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Convert a NATS payload to a StoredNotification
   */
  payloadToNotification(payload: any, userId: string): StoredNotification {
    const data = payload?.data || payload;
    const id = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    return {
      id,
      userId,
      type: data?.type || 'notification',
      title: data?.title || 'Notification',
      body: data?.body || data?.message || '',
      data: data,
      timestamp: new Date(),
      read: false,
      caseId: data?.caseId,
      senderId: data?.senderId,
      senderName: data?.senderName
    };
  }

  private async ensureDbReady(): Promise<void> {
    if (!this.db && typeof indexedDB !== 'undefined') {
      await this.initDatabase();
    }
  }
}
